import { Product, OptionsBundle } from '../../stores/appReportsStore';

// ============================================================================
// Order-history sourcing improvement: unit-price tiering
// (used by OrderHistorySourcingView — strict g/ml/p comparability, no fudge)
// ============================================================================

export interface TieredOptions {
  cheaper: Product[];       // comparable unit + cheaper unit price (cheapest first)
  uncomparable: Product[];  // different unit family — cannot compare (best match first)
  expensive: Product[];     // comparable unit + same/higher unit price (least pricey first)
}

// Auto-select an alternative only when it is a genuinely good match.
const GOOD_MATCH_SCORE = 75;

/** Two products are comparable only within the same unit family: kg↔kg, l↔l, piece↔piece. */
function isComparable(parent: Product, option: Product): boolean {
  return parent.unitPriceUnit.toLowerCase() === option.unitPriceUnit.toLowerCase();
}

/** For comparable options, set quantity so the option covers the same total amount as the parent order. */
function adjustComparableQuantity(parent: Product, option: Product): Product {
  const parentStd = parent.useSingle ? parent.standardizedAmount : parent.combo?.standardizedAmount ?? parent.standardizedAmount;
  const parentAmount = parentStd * parent.quantity;
  const optStd = option.useSingle ? option.standardizedAmount : option.combo?.standardizedAmount ?? option.standardizedAmount;
  if (optStd > 0) {
    return { ...option, quantity: Number((parentAmount / optStd).toFixed(2)) };
  }
  return { ...option };
}

/**
 * Split a product's alternatives into the three tiers we want to surface:
 * comparable & cheaper → uncomparable → comparable & more expensive.
 * Ranking always uses unit price; uncomparable options carry no % (different std unit).
 */
export function buildTieredOptions(parent: Product, options: Product[]): TieredOptions {
  const cheaper: Product[] = [];
  const uncomparable: Product[] = [];
  const expensive: Product[] = [];

  for (const opt of options) {
    if (opt.articleId === parent.articleId) continue; // the parent itself is not an alternative
    // Comparable only with a real unit price on both sides and the same unit family.
    const canCompare = isComparable(parent, opt) && opt.unitPrice > 0 && parent.unitPrice > 0;
    if (canCompare) {
      const adjusted = adjustComparableQuantity(parent, opt);
      if (adjusted.unitPrice < parent.unitPrice) cheaper.push(adjusted);
      else expensive.push(adjusted);
    } else {
      uncomparable.push({ ...opt });
    }
  }

  cheaper.sort((a, b) => a.unitPrice - b.unitPrice);
  expensive.sort((a, b) => a.unitPrice - b.unitPrice);
  uncomparable.sort((a, b) => b.matchScore - a.matchScore);

  return { cheaper, uncomparable, expensive };
}

/**
 * Best switch = among comparable options that are both cheaper and a good match,
 * pick the BEST match (highest matchScore), tie-broken by lower unit price;
 * otherwise keep the parent.
 */
export function chooseBestOption(parent: Product, tiers: TieredOptions): Product {
  const goodCheaper = tiers.cheaper.filter(o => o.matchScore >= GOOD_MATCH_SCORE);
  if (goodCheaper.length === 0) return parent;
  return goodCheaper.reduce((best, current) => {
    if (current.matchScore !== best.matchScore) {
      return current.matchScore > best.matchScore ? current : best;
    }
    return current.unitPrice < best.unitPrice ? current : best;
  });
}

export function countTieredOptions(tiers: TieredOptions): number {
  return tiers.cheaper.length + tiers.uncomparable.length + tiers.expensive.length;
}


export function adjustBundleQuantities(
    parents: Record<string,Product>,
    bundles: Record<string,OptionsBundle>,
): Record<string,OptionsBundle> {
    const out: Record<string,OptionsBundle> = {}

    for (const [id, bundle] of Object.entries(bundles)) {
        const parent = parents[id]
        if (!parent) continue

        // adjust sourcingOptions
        const adjustedSourcing = adjustOptionsQuantities(
        parents,
        { [id]: bundle.sourcingOptions }
        )[id] || []

        // adjust searchOptions
        const adjustedSearch   = adjustOptionsQuantities(
        parents,
        { [id]: bundle.searchOptions }
        )[id] || []

        out[id] = {
        sourcingOptions: adjustedSourcing,
        searchOptions:   adjustedSearch,
        }
    }

    return out
}

export function adjustOptionsQuantities(parents: Record<string, Product>, productOptions: Record<string, Product[]>) : Record<string, Product[]> {
    const adjustedOptions: Record<string, Product[]> = {};
    for (const [id, sourcedOptions] of Object.entries(productOptions)) {
        const parent = parents[id];
        if (parent) {
            const parentUnitPriceUnit = parent.unitPriceUnit.toLowerCase();

            const parentStandardAmount = parent.useSingle ? parent.standardizedAmount : parent.combo?.standardizedAmount ?? parent.standardizedAmount;
            const parentAmount = parentStandardAmount * parent.quantity;

            // Create deep copies of the options to avoid mutating the original
            const adjustedSourcedOptions = sourcedOptions.map(option => ({ ...option }));

            for (const sourcedOption of adjustedSourcedOptions) {
                const sourcedOptionUnitPriceUnit = sourcedOption.unitPriceUnit.toLowerCase();
                const sourcedOptionStandardAmount = sourcedOption.useSingle ? sourcedOption.standardizedAmount : sourcedOption.combo?.standardizedAmount ?? sourcedOption.standardizedAmount;
                var quantityNeededForOption = 1

                switch (`${parentUnitPriceUnit}_vs_${sourcedOptionUnitPriceUnit}`) {
                    case "kg_vs_kg":
                    case "l_vs_l":
                    case "kg_vs_l":
                    case "l_vs_kg":
                      // both stdUnit should be same, we can do exact calculation
                      // parent is kg, sourcedOption is l, we can do exact calculation assuming 1ml = 1g (usually good approximation)
                      quantityNeededForOption = Number((parentAmount / sourcedOptionStandardAmount).toFixed(2));
                      sourcedOption.quantity = quantityNeededForOption;
                      break
                    case "piece_vs_piece":
                      // both stdUnit should be piece, we can do exact calculation (assuming the weight of each piece is the same, ok for now)
                      quantityNeededForOption = Number((parentAmount / sourcedOptionStandardAmount).toFixed(2));
                      sourcedOption.quantity = quantityNeededForOption;
                      break;
                    case "piece_vs_kg":
                    case "piece_vs_l":
                        // parent is piece, sourcedOption is l or kg, we do not know how much weight is a piece. This should not happen often.
                        // for now we use the estimatatedWeightPerPiece
                        var parentEstWeightPerPiece = parent.estimatedWeightPerPiece;
                        if (!parentEstWeightPerPiece || parentEstWeightPerPiece === 0) {
                            console.log(`${parent.articleId}: parent ${parent.name} has no estimatedWeightPerPiece, GONNA ASSUME 100g per piece`);
                            parentEstWeightPerPiece = 100;
                        }
                        quantityNeededForOption = Number(((parentAmount * parentEstWeightPerPiece) / sourcedOptionStandardAmount).toFixed(2));
                        sourcedOption.quantity = quantityNeededForOption;
                        break;
                    case "l_vs_piece":
                    case "kg_vs_piece":
                        // parent is l or kg, option is piece, we do not know how much weight is a piece. This can happen often.
                        // for now we use the estimatatedWeightPerPiece of the option
                        var optEstWeight = sourcedOption.estimatedWeightPerPiece;
                        if (!optEstWeight || optEstWeight === 0) {
                            console.log(`${sourcedOption.articleId}: option ${sourcedOption.name} has no estimatedWeightPerPiece, GONNA ASSUME 100g per piece`);
                            optEstWeight = 100;
                        }
                        quantityNeededForOption = Number((parentAmount / (sourcedOptionStandardAmount * optEstWeight)).toFixed(2));
                        sourcedOption.quantity = quantityNeededForOption;
                        break;
                    default:
                        console.log(`${parent.articleId}: parent ${parent.name}: UNHANDLED CASE ${parentUnitPriceUnit}_vs_${sourcedOptionUnitPriceUnit}`);
                      break;
                  }
            }
            
            // Store the adjusted options in the result
            adjustedOptions[id] = adjustedSourcedOptions;
        }
    }
    
    return adjustedOptions;
}


export function chooseInitialOptions(productsMap: Record<string, Product>, productOptions: Record<string, Product[]>) : Record<string, Product> {
    const initialSelectedOptions: Record<string, Product> = {};
    
    for (const [articleId, options] of Object.entries(productOptions)) {
        const parentProduct = productsMap[articleId];
        if (!parentProduct) {
            console.error(`No parent product found for articleId: ${articleId}`);
            continue;
        }
        
        // Find the parent product in the options (should always be there)
        const parentInOptions = options.find(option => option.articleId === articleId);
        if (!parentInOptions) {
            console.error(`Parent product ${articleId} not found in its own options list`);
            continue;
        }
        
        // Start with parent as default choice
        let selectedOption = parentInOptions;
        
        // Look for better options based on criteria
        const betterOptions = options.filter(option => 
            option.matchScore > 85 && 
            option.unitPriceUnit.toLowerCase() === parentProduct.unitPriceUnit.toLowerCase() && 
            option.unitPrice < parentProduct.unitPrice
        );
        
        if (betterOptions.length > 0) {
            // If there are multiple better options, select the one with highest matchScore
            selectedOption = betterOptions.reduce((best, current) => 
                current.matchScore > best.matchScore ? current : best
            );            
        }
        
        initialSelectedOptions[articleId] = selectedOption;
    }
    
    return initialSelectedOptions;
}